import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, ChevronDown, Loader2, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`premium-card border rounded-xl transition-all duration-200 ${open ? "border-amber-500/30 shadow-sm" : "hover:border-amber-500/30"}`}>
      <button type="button" onClick={() => setOpen(!open)} title={open ? "Collapse answer" : "Expand answer"}
        aria-expanded={open} className="w-full flex items-center justify-between p-4 text-left font-medium hover:bg-muted/50 transition-colors text-sm sm:text-base">
        <span>{q}</span>
        <span className={`shrink-0 ml-2 inline-flex h-6 w-6 items-center justify-center rounded-lg transition-all duration-300 ${open ? "bg-primary/10 text-primary rotate-180" : "text-muted-foreground"}`}>
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>
      {open && <div className="px-4 pb-4 text-muted-foreground text-sm animate-fade-up">{a}</div>}
    </div>
  );
}

export default function FAQ() {
  const [faqSections, setFaqSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadFaqs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/faqs");
      const items = data?.items || data?.data || data || [];
      const grouped = new Map();
      if (Array.isArray(items)) {
        items.forEach((item) => {
          if (!item?.question || !item?.answer) return;
          const category = item.category || "General";
          if (!grouped.has(category)) grouped.set(category, []);
          grouped.get(category).push({ q: item.question, a: item.answer });
        });
      }
      setFaqSections(Array.from(grouped, ([category, questions]) => ({ category, questions })));
    } catch (err) {
      setFaqSections([]);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFaqs(); }, [loadFaqs]);

  return (
    <div className="premium-page-surface min-h-[70vh]">
      <section className="border-b border-border bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
          <p className="text-xs font-black uppercase tracking-[0.24em] text-muted-foreground">Help centre</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-primary mt-2">Frequently Asked Questions</h1>
          <p className="text-muted-foreground mt-3">Current answers maintained by the ShreeHomes team.</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary" /> Loading FAQs…
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-8 text-center">
            <AlertCircle className="w-8 h-8 text-destructive mx-auto" />
            <h2 className="font-display text-xl font-bold mt-3">FAQs are temporarily unavailable</h2>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <Button onClick={loadFaqs} title="Retry loading FAQs" className="mt-5 gap-2"><RefreshCw className="w-4 h-4" /> Try again</Button>
          </div>
        ) : faqSections.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center">
            <h2 className="font-display text-xl font-bold">No published FAQs yet</h2>
            <p className="text-sm text-muted-foreground mt-2">Please check back after the support team publishes guidance.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {faqSections.map((section) => (
              <section key={section.category}>
                <h2 className="font-display text-xl font-bold mb-3">{section.category}</h2>
                <div className="space-y-2">
                  {section.questions.map((item, idx) => <FAQItem key={`${item.q}-${idx}`} q={item.q} a={item.a} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
